import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { toast } from 'react-toastify';
import DatePicker from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import DateObject from "react-date-object";
import { 
  createImageReportRequest, 
  getUserImageReportRequests, 
  downloadImageReport,
  ImageReportRequest 
} from '../services/imageReportService';
import { formatPersianDateTime, formatPersianDate } from '../utils/persianDate';
import ModernFileUploader from '../components/ui/ModernFileUploader';
import { validateIranianNationalCode } from '../utils/validation';

// Translate status to Persian
const translateStatus = (status: string): string => {
  const statusMap: Record<string, string> = {
    pending: 'در حال بررسی',
    approved: 'تایید شده',
    rejected: 'تایید نشده'
  };
  return statusMap[status] || status;
};

// Get status color
const getStatusColor = (status: string): string => {
  const colorMap: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800'
  };
  return colorMap[status] || 'bg-gray-100 text-gray-800';
};

const ReceiveImageReport: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'new' | 'history'>('new');
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState<ImageReportRequest[]>([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    admissionNumber: '',
    nationalId: '',
    firstName: '',
    lastName: '',
    testDate: '',
    description: '',
    receiptImage: null as File | null,
    receiptImageUrl: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Persian date state for test date
  const [selectedTestDate, setSelectedTestDate] = useState<DateObject | null>(null);

  // Check for openTab parameter on component mount
  useEffect(() => {
    const openTab = searchParams.get('openTab');
    if (openTab === '1') {
      setActiveTab('history');
    }
  }, [searchParams]);

  // Load user's requests on component mount
  useEffect(() => {
    if (activeTab === 'history') {
      fetchUserRequests();
    }
  }, [activeTab]);

  const fetchUserRequests = async () => {
    try {
      setLoadingRequests(true);
      const response = await getUserImageReportRequests({ limit: 20 });
      if (response.success) {
        setRequests(response.data);
      }
    } catch (error) {

      toast.error('خطا در دریافت لیست درخواست‌ها');
    } finally {
      setLoadingRequests(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleReceiptUpload = (file: File, fileUrl: string) => {
    setFormData(prev => ({
      ...prev,
      receiptImage: file,
      receiptImageUrl: fileUrl
    }));
    
    // Clear any existing errors for the receipt image field
    if (errors.receiptImage) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.receiptImage;
        return newErrors;
      });
    }
  };

  // Handle Persian date change for test date
  const handleTestDateChange = (date: DateObject | null) => {
    setSelectedTestDate(date);
    if (date) {
      // Convert Persian date to Gregorian date string for backend
      const gregorianDate = date.toDate(); // This converts to JavaScript Date object
      const isoDate = gregorianDate.toISOString().split('T')[0]; // Format as YYYY-MM-DD
      setFormData(prev => ({
        ...prev,
        testDate: isoDate
      }));
      
      // Clear validation errors
      if (errors.testDate) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.testDate;
          return newErrors;
        });
      }
    } else {
      setFormData(prev => ({
        ...prev,
        testDate: ''
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    
    // Validation
    if (!formData.admissionNumber.trim()) {
      toast.error('شماره پذیرش الزامی است');
      return;
    }
    
    if (!formData.nationalId.trim()) {
      setErrors(prev => ({ ...prev, nationalId: 'کد ملی الزامی است' }));
      toast.error('کد ملی الزامی است');
      return;
    }
    const nationalIdValidation = validateIranianNationalCode(formData.nationalId.trim());
    if (!nationalIdValidation.isValid) {
      setErrors(prev => ({ ...prev, nationalId: nationalIdValidation.message }));
      toast.error(nationalIdValidation.message);
      return;
    }
    
    if (!formData.firstName.trim()) {
      toast.error('نام الزامی است');
      return;
    }
    
    if (!formData.lastName.trim()) {
      toast.error('نام خانوادگی الزامی است');
      return;
    }
    
    if (!formData.testDate || !selectedTestDate) {
      toast.error('تاریخ انجام آزمایش الزامی است');
      setErrors(prev => ({ ...prev, testDate: 'تاریخ انجام آزمایش الزامی است' }));
      return;
    }
    
    if (!formData.receiptImage) {
      toast.error('آپلود تصویر قبض الزامی است');
      return;
    }

    try {
      setLoading(true);
      
      const requestData = {
        patientInfo: {
          admissionNumber: formData.admissionNumber.trim(),
          nationalId: formData.nationalId.trim(),
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          testDate: formData.testDate
        },
        description: formData.description.trim(),
        receiptImage: formData.receiptImage
      };

      const response = await createImageReportRequest(requestData);
      
      if (response.success) {
        toast.success('درخواست با موفقیت ثبت شد');
        
        // Reset form
        setFormData({
          admissionNumber: '',
          nationalId: '',
          firstName: '',
          lastName: '',
          testDate: '',
          description: '',
          receiptImage: null,
          receiptImageUrl: ''
        });
        setSelectedTestDate(null);
        
        // Switch to history tab to see the new request
        setActiveTab('history');
      }
    } catch (error: any) {

      const errorMessage = error.response?.data?.message || 'خطا در ثبت درخواست';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (requestId: string) => {
    try {
      await downloadImageReport(requestId);
      toast.success('فایل با موفقیت دانلود شد');
    } catch (error: any) {

      const errorMessage = error.response?.data?.message || 'خطا در دانلود فایل';
      toast.error(errorMessage);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-4">
        <div className="card bg-white rounded-sm p-4 border border-slate-300">
          <h2 className="text-lg font-bold text-slate-800 mb-4">دریافت تصویر جواب آزمایش</h2>
          
          {/* Tab Navigation */}
          <div className="flex space-x-1 mb-6">
            <button
              onClick={() => setActiveTab('new')}
              className={`px-4 py-2 rounded-md font-medium ${
                activeTab === 'new'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              ایجاد درخواست جدید
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`px-4 py-2 rounded-md font-medium ${
                activeTab === 'history'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              تاریخچه درخواست‌ها
            </button>
          </div>

          {activeTab === 'new' && (
            <div>
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-blue-500 ml-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <p className="text-blue-700 text-sm">
                    لطفا اطلاعات بیمار و تصویر قبض را با دقت وارد کنید. پس از بررسی توسط ادمین، نتیجه به شما اطلاع داده خواهد شد.
                  </p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Patient Information */}
                <div className="bg-gray-50 p-4 rounded-md">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">مشخصات بیمار</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        شماره پذیرش *
                      </label>
                      <input
                        type="text"
                        name="admissionNumber"
                        value={formData.admissionNumber}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="شماره پذیرش را وارد کنید"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        کد ملی *
                      </label>
                      <input
                        type="text"
                        name="nationalId"
                        value={formData.nationalId}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                          errors['nationalId']
                            ? 'border-red-500 bg-red-50 focus:ring-red-500'
                            : formData.nationalId && validateIranianNationalCode(formData.nationalId).isValid
                            ? 'border-green-500 bg-green-50 focus:ring-green-500'
                            : 'border-gray-300 focus:ring-blue-500'
                        }`}
                        placeholder="کد ملی را وارد کنید"
                        maxLength={10}
                        required
                      />
                      {errors['nationalId'] && (
                        <p className="text-red-500 text-sm mt-1">{errors['nationalId']}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        نام *
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="نام را وارد کنید"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        نام خانوادگی *
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="نام خانوادگی را وارد کنید"
                        required
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        تاریخ انجام آزمایش *
                      </label>
                      <DatePicker
                        value={selectedTestDate}
                        onChange={handleTestDateChange}
                        calendar={persian}
                        locale={persian_fa}
                        format="YYYY/MM/DD"
                        placeholder="انتخاب تاریخ آزمایش"
                        inputClass="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        containerStyle={{
                          width: "100%"
                        }}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        تقویم شمسی - تاریخ انجام آزمایش را از تقویم انتخاب کنید
                      </p>
                      {errors.testDate && (
                        <p className="text-red-500 text-sm mt-1">{errors.testDate}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Receipt Image Upload */}
                <div className="bg-gray-50 p-4 rounded-md">
                  <ModernFileUploader
                    id="receiptImage"
                    label="آپلود تصویر قبض"
                    onChange={handleReceiptUpload}
                    required={true}
                    error={errors.receiptImage}
                    accept="image/*,application/pdf"
                  />
                </div>

                {/* Description */}
                <div className="bg-gray-50 p-4 rounded-md">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">توضیحات (اختیاری)</h3>
                  
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="توضیحات اضافی در صورت نیاز..."
                  />
                </div>

                {/* Submit Button */}
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-3 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'در حال ثبت...' : 'ثبت درخواست'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {activeTab === 'history' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">تاریخچه درخواست‌ها</h3>
              
              {loadingRequests ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : requests.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-sm">هنوز درخواستی ثبت نکرده‌اید</p>
                  <button
                    onClick={() => setActiveTab('new')}
                    className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                  >
                    ایجاد درخواست جدید
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">ردیف</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">شماره پذیرش</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">بیمار</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">وضعیت</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">تاریخ درخواست</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">تاریخ پاسخ</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">عملیات</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {requests.map((request, index) => (
                        <tr key={request._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {index + 1}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {request.patientInfo.admissionNumber}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div>
                              <div className="font-medium">
                                {request.patientInfo.firstName} {request.patientInfo.lastName}
                              </div>
                              <div className="text-xs text-gray-500">
                                کد ملی: {request.patientInfo.nationalId}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs rounded-full font-medium ${getStatusColor(request.status)}`}>
                              {translateStatus(request.status)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatPersianDateTime(request.createdAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {request.adminResponse?.responseDate 
                              ? formatPersianDateTime(request.adminResponse.responseDate)
                              : '---'
                            }
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="space-y-1">
                              {request.status === 'approved' && request.adminResponse?.approvedReportPdf && (
                                <button
                                  onClick={() => handleDownload(request._id)}
                                  className="inline-flex items-center px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-md hover:bg-green-200 transition-colors"
                                >
                                  <svg className="w-3 h-3 ml-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                                  </svg>
                                  دانلود PDF
                                </button>
                              )}
                              {request.status === 'rejected' && request.adminResponse?.rejectionReason && (
                                <div className="text-xs text-red-600">
                                  <strong>دلیل عدم تایید:</strong> {request.adminResponse.rejectionReason}
                                </div>
                              )}
                              {request.description && (
                                <div className="text-xs text-gray-500">
                                  <strong>توضیحات:</strong> {request.description}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              {requests.length > 0 && (
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={fetchUserRequests}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                  >
                    بارگذاری مجدد
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ReceiveImageReport; 